import React, { useState } from 'react';
import GeneralSection from './Sections/GeneralSection';
import StatusPanel from '../StatusPanel';
import PageWrapper from '../../Layout/PageWrapper';
import JsonEditor from '../JsonEditor';

// Mirrors the `quality_threshold` default on the FeatureExtractionJob model.
export const DEFAULT_QUALITY_THRESHOLD = 50.0;

const FeatureExtractionJobForm = ({
  // Core props
  formData,
  setFormData,
  isEditing,

  // Job-related props (for edit mode)
  job = null,

  // Page-specific props
  title,
  showEditButtons = false,
  editControls = null,

  // Form submission
  onSubmit = null,
  submitText = 'Submit',
  submitLoading = false,
  error = ''
}) => {
  // JSON Editor state
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);

  const isCreateMode = !job;

  // Handle form data changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle JSON editor changes
  const handleJsonApply = (newFormData) => {
    setFormData(newFormData);
  };

  return (
    <PageWrapper>
      {/* Status Panel - Top on small screens */}
      <div className="lg:hidden">
        <StatusPanel job={job} isCreateMode={isCreateMode} />
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content Area */}
        <div className="flex-1">
          <div className="mb-6 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <div className="flex gap-3">
              {/* JSON Editor Button - only show for non-create mode */}
              {!isCreateMode && (
                <button
                  onClick={() => setIsJsonEditorOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Edit form data as JSON"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  JSON
                </button>
              )}

              {/* Existing edit controls */}
              {showEditButtons && editControls && editControls}
            </div>
          </div>

          {onSubmit ? (
            <form onSubmit={onSubmit} className="space-y-6">
              <FormSections
                formData={formData}
                isEditing={isEditing}
                onInputChange={handleInputChange}
                job={job}
                isCreateMode={isCreateMode}
              />

              {/* Submit Button - only shown for create mode */}
              {isCreateMode && (
                <div className="flex justify-center pt-4">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="bg-black text-white rounded-lg px-8 py-3 text-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitLoading ? `${submitText}…` : submitText}
                  </button>
                </div>
              )}

              {error && (
                <div className="text-red-600 text-center">{error}</div>
              )}
            </form>
          ) : (
            <div className="space-y-6">
              <FormSections
                formData={formData}
                isEditing={isEditing}
                onInputChange={handleInputChange}
                job={job}
                isCreateMode={isCreateMode}
              />

              {error && (
                <div className="text-red-600 text-center mt-4">{error}</div>
              )}
            </div>
          )}
        </div>

        {/* Status Panel - Right side on large screens */}
        <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">
          <div className="sticky top-6">
            <StatusPanel job={job} isCreateMode={isCreateMode} />
          </div>
        </div>
      </div>

      {/* JSON Editor Modal */}
      <JsonEditor
        isOpen={isJsonEditorOpen}
        onClose={() => setIsJsonEditorOpen(false)}
        formData={formData}
        onApply={handleJsonApply}
      />
    </PageWrapper>
  );
};

// Separate component for the form sections to keep the main component clean
const FormSections = ({ formData, isEditing, onInputChange, job, isCreateMode }) => (
  <>
    <GeneralSection
      formData={formData}
      isEditing={isEditing}
      onInputChange={onInputChange}
    />
  </>
);

export default FeatureExtractionJobForm;
